import React from 'react';
import DodTermText from '../DodTermText';
import type { DodGlossaryTermId } from '../dodGlossary';
import { kidExplain, noteCanStandInForProof } from '../kidExplain';
import {
  isItemCompleteLocked,
  taskCredentialsNeed,
  taskNeedsVaultCredentials,
  type ComplianceChecklistItemDef,
  type ComplianceStepEvidence,
} from '../constants/complianceChecklists';

type AssistMode = 'explain' | 'do';

type StagedAction = {
  action_id: string;
  action_type: string;
  status: string;
  title: string;
  detail?: string;
  payload?: {
    email?: { to?: string; subject?: string; body?: string };
    draft?: { title?: string; text?: string };
    executor?: string;
  };
  result?: {
    franchise?: {
      mode?: string;
      right_to_transact?: unknown;
      message?: string;
      taxpayer_name?: string;
      taxpayer_id?: string;
      sos_registration_status?: string;
      sos_file_number?: string;
      state_of_formation?: string;
      artifact_id?: string;
      proposal_id?: string;
    };
    error?: unknown;
    [key: string]: unknown;
  };
  artifact_id?: string;
  error?: string;
};

type TaskFinding = {
  kind?: string;
  label?: string;
  value?: string | null;
  status?: string;
  summary?: string;
  fields?: Record<string, unknown>;
  artifact_id?: string;
  proposal_id?: string;
};

/** Prefer API findings; otherwise rebuild franchise evidence from executed actions. */
const findingsFromActions = (actions: StagedAction[]): TaskFinding[] => {
  const out: TaskFinding[] = [];
  for (const action of actions) {
    if (action.status !== 'executed' && action.status !== 'failed') continue;
    const franchise = action.result?.franchise;
    if (franchise) {
      const right = franchise.right_to_transact;
      const mode = String(franchise.mode || '').trim();
      if (
        action.status === 'failed'
        || mode === 'needs_credentials'
        || mode === 'needs_browser_bot'
        || mode === 'api_error'
      ) {
        out.push({
          kind: 'tx_franchise',
          label: 'Texas franchise tax account',
          value: null,
          status: 'needs_credentials',
          summary: String(
            franchise.message
            || action.result?.error
            || 'Comptroller lookup needs the API key or your printout. Nothing was saved as proof.',
          ),
          fields: { mode: mode || 'needs_credentials', message: franchise.message },
          artifact_id: undefined,
          proposal_id: franchise.proposal_id,
        });
      } else if (right != null && String(right).trim() !== '') {
        out.push({
          kind: 'tx_franchise',
          label: 'Texas franchise tax — right to transact',
          value: String(right),
          status: 'ok',
          summary: `Texas franchise tax account status (right to transact): ${right}.`,
          fields: {
            right_to_transact: String(right),
            mode: mode || undefined,
            taxpayer_name: franchise.taxpayer_name,
            taxpayer_id: franchise.taxpayer_id,
            sos_registration_status: franchise.sos_registration_status,
            sos_file_number: franchise.sos_file_number,
            state_of_formation: franchise.state_of_formation,
            message: franchise.message,
          },
          artifact_id: franchise.artifact_id || action.artifact_id,
          proposal_id: franchise.proposal_id,
        });
      }
    }
  }
  return out;
};

const ExplainIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
    <path fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" d="M6 16.5 4.5 20l3.6-1.2A8 8 0 1 0 6 16.5Z" />
    <path fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" d="M9 10.5h6M9 13.5h4" />
  </svg>
);

const DoIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
    <path fill="currentColor" d="M8 5.5v13l11-6.5-11-6.5Z" />
  </svg>
);

export const TaskAssistButtons: React.FC<{
  language: 'en' | 'es';
  mode: AssistMode | null;
  onExplain: () => void;
  onDo: () => void;
  labeled?: boolean;
}> = ({ language, mode, onExplain, onDo, labeled = false }) => {
  const es = language === 'es';
  const explainLabel = es ? 'Explícamelo' : 'Explain it to me';
  const doLabel = es ? 'Hazlo por mí' : 'Do it for me';
  return (
    <div
      className={`compliance-task-assists${labeled ? ' is-labeled' : ''}`}
      onClick={(event) => event.stopPropagation()}
      onKeyDown={(event) => event.stopPropagation()}
    >
      <button
        type="button"
        className={`compliance-task-assist${labeled ? ' is-labeled' : ''}${mode === 'explain' ? ' is-on' : ''}`}
        aria-pressed={mode === 'explain'}
        aria-label={explainLabel}
        onClick={onExplain}
      >
        <ExplainIcon />
        {labeled ? <span>{explainLabel}</span> : <span className="icon-tip" aria-hidden="true">{explainLabel}</span>}
      </button>
      <button
        type="button"
        className={`compliance-task-assist${labeled ? ' is-labeled' : ''}${mode === 'do' ? ' is-on' : ''}`}
        aria-pressed={mode === 'do'}
        aria-label={doLabel}
        onClick={onDo}
      >
        <DoIcon />
        {labeled ? <span>{doLabel}</span> : <span className="icon-tip" aria-hidden="true">{doLabel}</span>}
      </button>
    </div>
  );
};

/** Prefer the working page (docs) over the instructions page (source). */
export const officialPageUrl = (item: {
  accessUrl?: string;
  sourceUrl?: string;
  docsUrl?: string;
}): string => (
  (item.accessUrl || item.docsUrl || item.sourceUrl || '').trim()
);

export const openOfficialPage = (url: string): void => {
  const target = url.trim();
  if (!target || typeof window === 'undefined') {
    return;
  }
  window.open(target, '_blank', 'noopener,noreferrer');
};

type GuidePart = {
  id: string;
  text: string;
  evidenceHint?: string;
  kind?: 'do' | 'proof' | string;
};

const guidePartsFor = (item: ComplianceChecklistItemDef): GuidePart[] => {
  if (item.verifySteps?.length) {
    return item.verifySteps;
  }
  if (item.actionSteps?.length) {
    return item.actionSteps.map((text, index) => ({
      id: `guide-${index}`,
      text,
      evidenceHint: item.evidenceHint,
      kind: 'do' as const,
    }));
  }
  if ((item.action || '').trim()) {
    return [{
      id: 'guide-action',
      text: item.action,
      evidenceHint: item.evidenceHint,
      kind: 'do' as const,
    }];
  }
  return [];
};

const actionTypeLabel = (type: string, es: boolean): string => {
  if (type === 'email') return es ? 'Correo' : 'Email';
  if (type === 'upload_evidence') return es ? 'Subir evidencia' : 'Upload evidence';
  if (type === 'collect') return es ? 'Obtener prueba' : 'Collect proof';
  if (type === 'draft_document') return es ? 'Borrador' : 'Draft document';
  if (type === 'portal_file') return es ? 'Archivo en portal' : 'Portal filing';
  if (type === 'mark_ready') return es ? 'Listo para revisar' : 'Ready for review';
  return type;
};

type TaskAssistPanelProps = {
  item: ComplianceChecklistItemDef;
  language: 'en' | 'es';
  mode: AssistMode;
  seenTerms?: Set<DodGlossaryTermId>;
  onSaveStep?: (stepId: string, evidence: ComplianceStepEvidence) => void;
  /** Merge several verify-step rows after a Do-it-for-me run (avoids stale single-step patches). */
  onMergeSteps?: (patch: Record<string, ComplianceStepEvidence>) => void;
  /**
   * When Do-it-for-me fills every required proof step, merge those steps and mark
   * the parent task finished in one update (avoids a stale in_progress race).
   */
  onAllEvidenceReady?: (patch: Record<string, ComplianceStepEvidence>) => void;
  apiBase?: string;
  accessToken?: string;
  agentSkill?: string;
  consoleKind?: string;
  companyId?: string | number;
  legalName?: string;
  ein?: string;
  onRegistryChanged?: () => void;
  /** Open Gates and credentials with this portal id. */
  onAddCredentials?: (portalId: string) => void;
  /**
   * When this item is the compliance calendar task, Do-it-for-me applies
   * published / cadence dates through this callback instead of inventing them.
   */
  onApplyCalendarDates?: (events: Array<{ id: string; title: string; date: string }>) => void;
  consoleKindForCalendar?: string;
  existingCalendarEvents?: Array<{ id: string; title: string; date: string; skipped?: boolean }>;
};

export const TaskAssistPanel: React.FC<TaskAssistPanelProps> = ({
  item,
  language,
  mode,
  seenTerms,
  onSaveStep,
  onMergeSteps,
  onAllEvidenceReady,
  apiBase,
  accessToken,
  agentSkill,
  consoleKind,
  companyId,
  legalName,
  ein,
  onRegistryChanged,
  onAddCredentials,
  onApplyCalendarDates,
  consoleKindForCalendar,
  existingCalendarEvents,
}) => {
  const es = language === 'es';
  const lines = kidExplain(item, language);
  const parts = guidePartsFor(item);
  const [index, setIndex] = React.useState(0);
  const [draft, setDraft] = React.useState('');
  const [saved, setSaved] = React.useState('');
  const [pageSummary, setPageSummary] = React.useState('');
  const [pageReading, setPageReading] = React.useState(false);
  const [pageReadFailed, setPageReadFailed] = React.useState(false);
  type StoryStepProgress = {
    id?: string;
    keyword?: string;
    text?: string;
    verb?: string;
    auto?: boolean;
    hitl?: boolean;
    title?: string;
    status?: string;
    action_id?: string;
  };
  type StoryPayload = {
    feature?: string;
    scenario?: string;
    background?: string;
    steps?: StoryStepProgress[];
  };
  const [agentBusy, setAgentBusy] = React.useState(false);
  const [agentNote, setAgentNote] = React.useState('');
  const [agentTip, setAgentTip] = React.useState('');
  const [agentError, setAgentError] = React.useState('');
  const [actions, setActions] = React.useState<StagedAction[]>([]);
  const [story, setStory] = React.useState<StoryPayload | null>(null);
  const [storyProgress, setStoryProgress] = React.useState<StoryStepProgress[]>([]);
  const [actionBusy, setActionBusy] = React.useState('');
  const [preparedOnce, setPreparedOnce] = React.useState(false);
  const [findings, setFindings] = React.useState<TaskFinding[]>([]);
  const [vaultHasSecret, setVaultHasSecret] = React.useState<boolean | null>(null);
  const stagedRef = React.useRef<HTMLDivElement | null>(null);
  const part = parts[index];
  const workUrl = officialPageUrl(item);
  const instructionsUrl = (item.sourceUrl || '').trim();
  const showInstructionsLink = Boolean(instructionsUrl && instructionsUrl !== workUrl);
  const base = (apiBase || '').replace(/\/$/, '');
  const kind = String(consoleKind || '').trim();
  const credNeed = taskCredentialsNeed(item);
  const portalId = String(item.portalId || '').trim();
  const needsVault = taskNeedsVaultCredentials(item) && Boolean(portalId);

  const loadActions = React.useCallback(async (): Promise<StagedAction[]> => {
    if (!base || !accessToken || !kind) return [];
    try {
      const response = await fetch(
        `${base}/api/v1/compliance-registry/${encodeURIComponent(kind)}/controls/${encodeURIComponent(item.id)}/actions?status=staged${companyId ? `&company_id=${companyId}` : ''}`,
        { headers: { Authorization: `Bearer ${accessToken}` } },
      );
      if (!response.ok) return [];
      const body = await response.json() as { actions?: StagedAction[] };
      const next = Array.isArray(body.actions) ? body.actions : [];
      setActions(next);
      return next;
    } catch {
      return [];
    }
  }, [accessToken, base, companyId, item.id, kind]);

  React.useEffect(() => {
    if (!needsVault || !base || !accessToken || mode !== 'do') {
      setVaultHasSecret(null);
      return undefined;
    }
    let cancelled = false;
    const load = async () => {
      try {
        const response = await fetch(`${base}/api/v1/agent/monitor/credentials`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        if (!response.ok) {
          if (!cancelled) setVaultHasSecret(false);
          return;
        }
        const body = await response.json() as {
          credentials?: Array<{ portal_id?: string; has_secret?: boolean }>;
        };
        const match = (Array.isArray(body.credentials) ? body.credentials : []).find(
          (row) => String(row.portal_id || '').trim().toLowerCase() === portalId.toLowerCase(),
        );
        if (!cancelled) setVaultHasSecret(Boolean(match?.has_secret));
      } catch {
        if (!cancelled) setVaultHasSecret(false);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [accessToken, base, mode, needsVault, portalId, preparedOnce]);

  React.useEffect(() => {
    setIndex(0);
    setDraft('');
    setSaved('');
    setPageSummary('');
    setPageReading(false);
    setPageReadFailed(false);
    setVaultHasSecret(null);
    setAgentNote('');
    setAgentTip('');
    setAgentError('');
    setStory(null);
    setStoryProgress([]);
    setFindings([]);
    setPreparedOnce(false);
  }, [item.id, mode]);

  React.useEffect(() => {
    if (mode === 'do') void loadActions();
  }, [loadActions, mode]);

  React.useEffect(() => {
    if (!actions.length || !stagedRef.current) return;
    stagedRef.current.scrollIntoView?.({ behavior: 'smooth', block: 'nearest' });
  }, [actions.length]);

  React.useEffect(() => {
    if ((mode !== 'do' && mode !== 'explain') || !workUrl) {
      return undefined;
    }
    if (!base || !accessToken) {
      return undefined;
    }
    let cancelled = false;
    setPageReading(true);
    setPageReadFailed(false);
    setPageSummary('');
    void fetch(`${base}/api/v1/compliance-consoles/read-official-page`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ url: workUrl, task: item.action || item.title }),
    }).then(async (response) => {
      if (!response.ok) {
        throw new Error('read failed');
      }
      const body = await response.json();
      const summary = typeof body?.summary === 'string' ? body.summary.trim() : '';
      if (!cancelled) {
        setPageSummary(summary);
      }
    }).catch(() => {
      if (!cancelled) {
        setPageReadFailed(true);
      }
    }).finally(() => {
      if (!cancelled) {
        setPageReading(false);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [accessToken, base, item.action, item.id, item.title, mode, workUrl]);

  const markedCompleteRef = React.useRef(false);

  const commitEvidencePatch = React.useCallback((
    patch: Record<string, ComplianceStepEvidence>,
    note?: string,
  ) => {
    const ready = !isItemCompleteLocked(item, { stepProgress: patch });
    if (ready && onAllEvidenceReady && !markedCompleteRef.current) {
      markedCompleteRef.current = true;
      onAllEvidenceReady(patch);
      if (note) {
        setAgentNote(es
          ? `${note} Tarea marcada como terminada.`
          : `${note} Task marked finished.`);
      }
      return;
    }
    onMergeSteps?.(patch);
  }, [es, item, onAllEvidenceReady, onMergeSteps]);

  const applyFranchiseEvidence = React.useCallback((note: string) => {
    const stamp = new Date().toISOString();
    commitEvidencePatch({
      'given-identity': {
        done: true,
        doneAt: stamp,
        inferred: true,
        evidenceNote: es ? 'Identidad confirmada en el proyecto' : 'Identity confirmed on the project',
      },
      'when-lookup': {
        done: true,
        doneAt: stamp,
        inferred: true,
        evidenceNote: note,
      },
      'then-vault': {
        done: true,
        doneAt: stamp,
        inferred: true,
        evidenceNote: note,
      },
    }, note);
  }, [commitEvidencePatch, es]);

  /** When every Do-it-for-me substep is finished, mark the parent checklist task complete. */
  const tryCompleteFromStory = React.useCallback((
    progress: StoryStepProgress[],
    nextActions: StagedAction[],
    note?: string,
  ) => {
    if (!onAllEvidenceReady || markedCompleteRef.current) return;
    const steps = item.verifySteps || [];
    if (!steps.length) return;
    if (nextActions.some((row) => row.status === 'staged')) return;
    if (!progress.length) return;
    const unfinished = progress.some((step) => {
      const status = String(step.status || '');
      return status === 'pending' || status === 'staged' || status === 'blocked' || status === 'failed';
    });
    if (unfinished) return;
    const executed = progress.filter((step) => String(step.status || '') === 'executed');
    if (!executed.length) return;

    const stamp = new Date().toISOString();
    const evidenceNote = (note || agentNote || (es ? 'Hecho con Do it for me' : 'Finished with Do it for me')).trim();
    const patch: Record<string, ComplianceStepEvidence> = {};
    for (const step of steps) {
      const match = progress.find((row) => String(row.id || '') === step.id);
      if (match && String(match.status || '') === 'executed') {
        patch[step.id] = {
          done: true,
          doneAt: stamp,
          inferred: true,
          evidenceNote: String(match.title || match.text || evidenceNote),
        };
      }
    }
    // Story ids may not match verify-step ids — if the whole story ran, fill every verify step.
    if (Object.keys(patch).length < steps.length && executed.length === progress.length) {
      for (const step of steps) {
        if (patch[step.id]) continue;
        patch[step.id] = {
          done: true,
          doneAt: stamp,
          inferred: true,
          evidenceNote,
        };
      }
    }
    if (!Object.keys(patch).length) return;
    commitEvidencePatch(patch, evidenceNote);
  }, [agentNote, commitEvidencePatch, es, item.verifySteps, onAllEvidenceReady]);

  const askAgent = React.useCallback(async () => {
    if (agentBusy) return;
    setAgentBusy(true);
    setAgentError('');
    setAgentNote('');
    setAgentTip('');
    try {
      // Calendar task: apply published / cadence dates locally — do not invent SAM/COI/domain dates.
      if (onApplyCalendarDates && consoleKindForCalendar) {
        const { suggestCalendarDatesFor } = await import('../suggestCalendarDates');
        const fills = suggestCalendarDatesFor(
          consoleKindForCalendar,
          language,
          existingCalendarEvents || [],
        );
        setPreparedOnce(true);
        if (!fills.length) {
          setAgentNote(es
            ? 'Las fechas publicadas ya están en el calendario. Las renovaciones de SAM, dominio o seguro las anota usted con el aviso o el certificado.'
            : 'Published dates are already on the calendar. SAM, domain, and insurance renewals still need the notice or certificate date from you.');
          return;
        }
        onApplyCalendarDates(fills.map((row) => ({ id: row.id, title: row.title, date: row.date })));
        setAgentNote(es
          ? `Se agregaron ${fills.length} fecha(s) del calendario (vencimientos publicados y cadencias claras). Revise y ajuste si su aviso dice otra fecha.`
          : `Added ${fills.length} calendar date(s) from published deadlines and clear cadences. Review and change any date if your notice says otherwise.`);
        onRegistryChanged?.();
        return;
      }
      if (!base || !accessToken) {
        setAgentError(es ? 'Inicie sesión para continuar.' : 'Sign in to continue.');
        return;
      }
      if (kind) {
        const response = await fetch(
          `${base}/api/v1/compliance-registry/${encodeURIComponent(kind)}/tasks/complete`,
          {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${accessToken}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              control_id: item.id,
              ...(companyId ? { company_id: Number(companyId) } : {}),
              ...(legalName ? { legal_name: legalName } : {}),
              ...(ein ? { ein } : {}),
            }),
          },
        );
        const body = await response.json().catch(() => ({})) as {
          detail?: string;
          summary?: string;
          ai_tip?: string;
          findings?: TaskFinding[];
          actions?: StagedAction[];
          story?: StoryPayload;
          story_progress?: StoryStepProgress[];
          needs?: { note?: string };
          needs_approval?: boolean;
        };
        setPreparedOnce(true);
        if (!response.ok) {
          setAgentError(typeof body.detail === 'string' ? body.detail : (es
            ? 'No se pudo preparar el trabajo.'
            : 'Could not prepare the work.'));
          return;
        }
        const nextActions = Array.isArray(body.actions) ? body.actions : [];
        setActions(nextActions);
        setStory(body.story && typeof body.story === 'object' ? body.story : null);
        const nextStory = Array.isArray(body.story_progress) ? body.story_progress : [];
        setStoryProgress(nextStory);
        const apiFindings = Array.isArray(body.findings) ? body.findings : [];
        const nextFindings = apiFindings.length ? apiFindings : findingsFromActions(nextActions);
        setFindings(nextFindings);
        const awaiting = nextActions.filter((row) => row.status === 'staged');
        const ran = nextActions.filter((row) => row.status === 'executed');
        const franchiseFinding = nextFindings.find(
          (row) => row.kind === 'tx_franchise' && row.value,
        );
        const blockedFranchise = nextFindings.find(
          (row) => row.kind === 'tx_franchise' && row.status === 'needs_credentials',
        );
        const failedAction = nextActions.find((row) => row.status === 'failed');
        if (blockedFranchise || failedAction) {
          setAgentError(
            String(
              blockedFranchise?.summary
              || failedAction?.error
              || failedAction?.result?.error
              || (es
                ? 'La búsqueda no pudo terminarse. Nada se guardó como prueba.'
                : 'Lookup could not finish. Nothing was saved as proof.'),
            ),
          );
          setAgentNote(es
            ? 'La búsqueda automática no terminó (sin inicio de sesión de contribuyente). Abra la búsqueda pública y suba el PDF, o configure la clave API del servidor en el gateway.'
            : 'Automatic lookup did not finish (no taxpayer login). Open the public search and upload the PDF, or set the server API key on the gateway.');
          if (onMergeSteps && (item.verifySteps || []).some((step) => step.id === 'given-identity')) {
            const stamp = new Date().toISOString();
            onMergeSteps({
              'given-identity': {
                done: true,
                doneAt: stamp,
                inferred: true,
                evidenceNote: es ? 'Identidad confirmada en el proyecto' : 'Identity confirmed on the project',
              },
            });
          }
        } else {
          const doneNote = franchiseFinding?.summary
            || body.summary
            || (es
              ? (ran.length
                ? `NextCompli ejecutó ${ran.length} paso(s).${awaiting.length ? ` Quedan ${awaiting.length} para su aprobación.` : ''}`
                : `Listo: ${nextActions.length} paso(s).`)
              : (ran.length
                ? `NextCompli ran ${ran.length} step(s).${awaiting.length ? ` ${awaiting.length} still need your approval.` : ''}`
                : `Ready: ${nextActions.length} step(s).`));
          setAgentNote(doneNote);
          if (franchiseFinding?.value) {
            applyFranchiseEvidence(String(franchiseFinding.summary || franchiseFinding.value));
          } else {
            tryCompleteFromStory(nextStory, nextActions, doneNote);
          }
        }
        if (typeof body.ai_tip === 'string' && body.ai_tip.trim()) {
          setAgentTip(body.ai_tip.trim());
        }
        onRegistryChanged?.();
        return;
      }
      const skill = (agentSkill || '').trim();
      setPreparedOnce(true);
      if (!skill) {
        setAgentError(es ? 'No hay agente para esta consola.' : 'No agent for this console.');
        return;
      }
      const response = await fetch(`${base}/api/v1/agent/monitor/runs`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          skill,
          summary: `${item.title}: ${item.action || item.title}`.slice(0, 2000),
        }),
      });
      const body = await response.json().catch(() => ({})) as { detail?: string };
      if (!response.ok) {
        setAgentError(typeof body.detail === 'string' ? body.detail : (es
          ? 'No se pudo pedir al agente.'
          : 'Could not ask the agent.'));
        return;
      }
      setAgentNote(es
        ? 'Borrador en Agente → Necesita su decisión.'
        : 'Draft waiting under Agent → Needs your decision.');
    } catch {
      setPreparedOnce(true);
      setAgentError(es ? 'No se pudo pedir al agente.' : 'Could not ask the agent.');
    } finally {
      setAgentBusy(false);
    }
  }, [
    accessToken,
    agentBusy,
    agentSkill,
    base,
    companyId,
    consoleKindForCalendar,
    ein,
    es,
    existingCalendarEvents,
    item.action,
    item.id,
    item.title,
    item.verifySteps,
    kind,
    language,
    legalName,
    applyFranchiseEvidence,
    tryCompleteFromStory,
    onApplyCalendarDates,
    onMergeSteps,
    onRegistryChanged,
  ]);

  // One click on “Do it for me” should prepare work — do not wait for a second button.
  // If bulk Do-it-for-me (or a prior run) already staged work, reuse it instead of re-running.
  React.useEffect(() => {
    if (mode !== 'do' || preparedOnce || agentBusy) return;
    if (onApplyCalendarDates && consoleKindForCalendar) {
      void askAgent();
      return undefined;
    }
    if (!base || !accessToken || !(kind || agentSkill)) return undefined;
    let cancelled = false;
    void (async () => {
      const existing = await loadActions();
      if (cancelled) return;
      if (existing.length) {
        setPreparedOnce(true);
        setAgentNote(es
          ? 'Hay trabajo ya preparado para esta tarea. Revise y apruebe lo que falta.'
          : 'Work is already prepared for this task. Review and approve what is left.');
        return;
      }
      void askAgent();
    })();
    return () => {
      cancelled = true;
    };
  }, [accessToken, agentBusy, agentSkill, askAgent, base, consoleKindForCalendar, es, kind, loadActions, mode, onApplyCalendarDates, preparedOnce]);

  const decideAction = async (actionId: string, decision: 'approved' | 'rejected' | 'dismissed') => {
    if (!base || !accessToken || actionBusy) return;
    setActionBusy(actionId);
    setAgentError('');
    try {
      const response = await fetch(
        `${base}/api/v1/compliance-registry/actions/${encodeURIComponent(actionId)}/decide`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ decision }),
        },
      );
      const body = await response.json().catch(() => ({})) as {
        detail?: string;
        action?: StagedAction;
      };
      if (!response.ok) {
        setAgentError(typeof body.detail === 'string' ? body.detail : (es
          ? 'No se pudo ejecutar la acción.'
          : 'Could not run the action.'));
        return;
      }
      const updated = body.action;
      if (updated?.status === 'executed') {
        const nextFindings = findingsFromActions([updated]);
        if (nextFindings.length) {
          setFindings((prev) => {
            const kinds = new Set(nextFindings.map((row) => row.kind));
            return [...prev.filter((row) => !kinds.has(row.kind)), ...nextFindings];
          });
        }
        const franchise = updated.result?.franchise;
        if (franchise?.mode === 'needs_credentials') {
          setAgentNote(es
            ? 'La búsqueda del Comptroller no terminó. No hay login de contribuyente. Apruebe el paso de auto-servicio para subir el PDF público, o configure la clave API del servidor — no hay “Aceptar prueba” hasta que exista evidencia real.'
            : 'Comptroller lookup did not finish. There is no taxpayer login. Approve the self-serve step to upload the public PDF, or set the server API key — no “Accept proof” until real evidence exists.');
        } else if (franchise?.right_to_transact != null && String(franchise.right_to_transact).trim() !== '') {
          const note = es
            ? `Estado de la cuenta de impuesto franquicia de Texas (right to transact): ${franchise.right_to_transact}.`
            : `Texas franchise tax account status (right to transact): ${franchise.right_to_transact}.`;
          setAgentNote(note);
          applyFranchiseEvidence(note);
        } else {
          const note = es
            ? `Hecho dentro de NextCompli: ${updated.title}${updated.artifact_id ? ` · vault ${updated.artifact_id}` : ''}`
            : `Done inside NextCompli: ${updated.title}${updated.artifact_id ? ` · vault ${updated.artifact_id}` : ''}`;
          setAgentNote(note);
          const remaining = await loadActions();
          // No staged approvals left → every Do-it-for-me subtask is done; finish the parent task.
          if (!remaining.length && (item.verifySteps || []).length) {
            setStoryProgress((prev) => prev.map((step) => (
              step.status === 'failed' || step.status === 'blocked'
                ? step
                : { ...step, status: 'executed' }
            )));
            const stamp = new Date().toISOString();
            const patch: Record<string, ComplianceStepEvidence> = {};
            for (const step of item.verifySteps || []) {
              patch[step.id] = {
                done: true,
                doneAt: stamp,
                inferred: true,
                evidenceNote: note,
              };
            }
            commitEvidencePatch(patch, note);
          }
          onRegistryChanged?.();
          return;
        }
      } else if (updated?.status === 'failed') {
        setAgentError(String(updated.error || updated.result?.error || (es ? 'Falló la acción.' : 'Action failed.')));
      }
      await loadActions();
      onRegistryChanged?.();
    } catch {
      setAgentError(es ? 'No se pudo ejecutar la acción.' : 'Could not run the action.');
    } finally {
      setActionBusy('');
    }
  };

  const cardRef = React.useRef<HTMLElement | null>(null);
  React.useEffect(() => {
    if (mode !== 'do' && mode !== 'explain') return;
    const node = cardRef.current;
    if (node && typeof node.scrollIntoView === 'function') {
      node.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [mode]);

  if (mode === 'explain') {
    return (
      <section
        ref={cardRef}
        className="task-assist-card"
        aria-label={es ? 'Explícamelo' : 'Explain it to me'}
      >
        <h4>{es ? 'Explícamelo' : 'Explain it to me'}</h4>
        <ol className="task-assist-lines">
          {lines.map((line) => (
            <li key={line}><DodTermText text={line} language={language} seen={seenTerms} /></li>
          ))}
        </ol>
        {pageReading ? <p>{es ? 'Leyendo la página oficial…' : 'Reading the official page…'}</p> : null}
        {pageSummary ? (
          <p className="task-assist-ask">
            <DodTermText text={pageSummary} language={language} seen={seenTerms} />
          </p>
        ) : null}
      </section>
    );
  }

  const noteOk = noteCanStandInForProof(part?.evidenceHint || item.evidenceHint || '');
  const fileId = `task-file-${item.id}-${part?.id || 'proof'}`;
  const awaitingActions = actions.filter((row) => row.status === 'staged');
  const doneCount = storyProgress.filter((step) => step.status === 'executed').length;
  const blockedCount = storyProgress.filter((step) => (
    step.status === 'blocked' || step.status === 'failed' || step.status === 'needs_heal'
  )).length;
  const lookupFailed = findings.some((row) => row.status === 'needs_credentials')
    || storyProgress.some((step) => (
      String(step.id || '') === 'when-lookup'
      && (step.status === 'blocked' || step.status === 'failed')
    ));
  const selfServeOnly = lookupFailed && awaitingActions.every((row) => (
    row.action_type === 'upload_evidence'
    || /printout|lookup could not|did not finish/i.test(String(row.title || ''))
  ));
  const showStory = Boolean(storyProgress.length || story);

  return (
    <section
      ref={cardRef}
      className={`task-assist-card${agentBusy ? ' is-running' : ''}${preparedOnce && !agentBusy ? ' is-done-run' : ''}${blockedCount ? ' is-needs-you' : ''}`}
      aria-label={es ? 'Hazlo por mí' : 'Do it for me'}
    >
      <header className="task-assist-head">
        <h4>{es ? 'NextCompli trabaja esta tarea' : 'NextCompli works this task'}</h4>
        <p className="task-assist-head-lead">
          {es
            ? 'Una sola pista: búsqueda, bóveda y lo que usted debe aprobar. No hay una segunda lista de pasos abajo.'
            : 'One track: lookup, vault, and anything you must approve. There is no second checklist below.'}
        </p>
      </header>

      {credNeed !== 'none' ? (
        <div
          className={`task-assist-cred-banner${needsVault && vaultHasSecret === false ? ' is-missing' : ''}`}
          role="status"
        >
          <strong>
            {needsVault
              ? (es ? 'Credenciales en la bóveda' : 'Vault credentials')
              : (es ? 'Inicio de sesión del operador' : 'Operator login')}
          </strong>
          <p>
            {needsVault
              ? (vaultHasSecret
                ? (es
                  ? `Hay un secreto guardado para “${portalId}”. Do-it-for-me puede usarlo tras su aprobación.`
                  : `A secret is on file for “${portalId}”. Do-it-for-me can use it after you approve.`)
                : (es
                  ? `Esta tarea necesita credenciales de “${item.accessLabel || portalId}” en Puertas y credenciales antes de que el agente entre al sitio.`
                  : `This task needs “${item.accessLabel || portalId}” credentials in Gates and credentials before the agent can enter the site.`))
              : (es
                ? 'Usted inicia sesión en el sitio de trabajo. El agente no guarda la contraseña a menos que la ponga en la bóveda.'
                : 'You sign into the work site. The agent does not store the password unless you vault it.')}
          </p>
          {needsVault && vaultHasSecret === false && onAddCredentials ? (
            <button
              type="button"
              className="dashboard-btn dashboard-btn-primary"
              onClick={() => onAddCredentials(portalId)}
            >
              {es ? 'Agregar credenciales' : 'Add credentials'}
            </button>
          ) : null}
          {workUrl ? (
            <a className="dashboard-btn" href={workUrl} target="_blank" rel="noopener noreferrer">
              {item.accessLabel || (es ? 'Abrir sitio de trabajo' : 'Open work site')}
            </a>
          ) : null}
        </div>
      ) : null}

      {(agentBusy || Boolean(actionBusy)) ? (
        <div className="task-assist-result is-busy" role="status" aria-live="polite">
          <span className="task-assist-spinner" aria-hidden="true" />
          <div>
            <strong>{es ? 'En curso' : 'Working now'}</strong>
            <p>
              {es
                ? 'NextCompli está ejecutando los pasos de esta historia (búsqueda, bóveda). Espere un momento…'
                : 'NextCompli is running this story’s steps (lookup, vault). Hang on a moment…'}
            </p>
          </div>
        </div>
      ) : null}

      {!agentBusy && preparedOnce ? (
        <div className="task-assist-result" role="status" aria-live="polite">
          <strong>
            {blockedCount
              ? (es ? 'Listo — necesita su ayuda' : 'Done — needs you')
              : awaitingActions.length
                ? (es ? 'Listo — apruebe lo que falta' : 'Done — approve what’s left')
                : (es ? 'Listo' : 'Done')}
          </strong>
          <p>
            {agentNote || (es
              ? `${doneCount} paso(s) hechos.${onAllEvidenceReady ? ' La tarea se marca terminada cuando la prueba está lista.' : ''}`
              : `${doneCount} step(s) finished.${onAllEvidenceReady ? ' The task is marked done when proof is ready.' : ''}`)}
          </p>
          {findings.length ? (
            <div className="task-assist-findings" role="region" aria-label={es ? 'Evidencia encontrada' : 'Evidence found'}>
              <h5>{es ? 'Lo que encontró' : 'What we found'}</h5>
              <ul className="task-assist-findings-list">
                {findings.map((row, index) => {
                  const fields = row.fields && typeof row.fields === 'object' ? row.fields : {};
                  const right = row.value || (fields.right_to_transact != null ? String(fields.right_to_transact) : '');
                  const taxpayer = fields.taxpayer_name != null ? String(fields.taxpayer_name) : '';
                  const sos = fields.sos_registration_status != null ? String(fields.sos_registration_status) : '';
                  const mode = fields.mode != null ? String(fields.mode) : '';
                  const fileNo = fields.sos_file_number != null ? String(fields.sos_file_number) : '';
                  return (
                    <li key={`${row.kind || 'finding'}-${index}`}>
                      <strong>{row.label || (es ? 'Hallazgo' : 'Finding')}</strong>
                      {right ? (
                        <p className="task-assist-finding-status">
                          <span>{es ? 'Estado' : 'Status'}</span>
                          {' '}
                          <em>{right}</em>
                        </p>
                      ) : row.summary ? (
                        <p>{row.summary}</p>
                      ) : null}
                      <dl className="task-assist-finding-dl">
                        {taxpayer ? (
                          <>
                            <dt>{es ? 'Contribuyente' : 'Taxpayer'}</dt>
                            <dd>{taxpayer}</dd>
                          </>
                        ) : null}
                        {sos ? (
                          <>
                            <dt>{es ? 'Registro SOS' : 'SOS registration'}</dt>
                            <dd>{sos}</dd>
                          </>
                        ) : null}
                        {fileNo ? (
                          <>
                            <dt>{es ? 'Núm. SOS' : 'SOS file #'}</dt>
                            <dd>{fileNo}</dd>
                          </>
                        ) : null}
                        {mode ? (
                          <>
                            <dt>{es ? 'Fuente' : 'Source'}</dt>
                            <dd>
                              {mode === 'api'
                                ? (es ? 'Comptroller de Texas (API)' : 'Texas Comptroller (API)')
                                : mode}
                            </dd>
                          </>
                        ) : null}
                        {row.artifact_id ? (
                          <>
                            <dt>{es ? 'Bóveda' : 'Vault'}</dt>
                            <dd><code>{row.artifact_id}</code></dd>
                          </>
                        ) : null}
                      </dl>
                    </li>
                  );
                })}
              </ul>
            </div>
          ) : null}
          {agentTip && !blockedCount ? (
            <p className="task-assist-result-tip">
              <span>{es ? 'Sugerencia' : 'Tip'}</span>
              {agentTip}
            </p>
          ) : null}
        </div>
      ) : null}

      {!agentBusy && !actionBusy && !preparedOnce && base && accessToken && (kind || agentSkill) ? (
        <div className="task-assist-result is-busy" role="status">
          <span className="task-assist-spinner" aria-hidden="true" />
          <div>
            <strong>{es ? 'Iniciando…' : 'Starting…'}</strong>
            <p>{es ? 'Preparando los pasos de esta tarea.' : 'Preparing the steps for this task.'}</p>
          </div>
        </div>
      ) : null}

      {agentError ? <p className="dashboard-form-error" role="alert">{agentError}</p> : null}

      {showStory ? (
        <div className="task-assist-story" role="region" aria-label={es ? 'Pasos que NextCompli corre' : 'Steps NextCompli runs'}>
          <h5 className="task-assist-story-feature">
            {story?.feature || (es ? 'Pasos' : 'Steps')}
          </h5>
          <ol className="task-assist-story-steps">
            {(storyProgress.length ? storyProgress : story?.steps || []).map((step) => {
              const status = String(step.status || (agentBusy ? 'pending' : 'pending'));
              const label = String(step.title || step.text || '').trim();
              return (
                <li key={step.id || `${step.keyword}-${step.text}`} className={`is-${status}`}>
                  <span className="task-assist-story-text">{label}</span>
                  <span className="task-assist-story-status">
                    {status === 'executed' ? (es ? 'Hecho' : 'Done')
                      : status === 'blocked' ? (es ? 'Bloqueado' : 'Blocked')
                        : status === 'staged' ? (es ? 'Espera usted' : 'Needs you')
                          : status === 'needs_heal' ? (es ? 'Hay que corregir' : 'Needs fix')
                            : status === 'dismissed' ? (es ? 'Omitido' : 'Skipped')
                              : status === 'failed' ? (es ? 'Falló' : 'Failed')
                              : agentBusy ? (es ? '…' : '…')
                                : (es ? 'Pendiente' : 'Pending')}
                  </span>
                </li>
              );
            })}
          </ol>
        </div>
      ) : null}

      {actions.some((row) => row.status === 'staged') ? (
        <div
          ref={stagedRef}
          className={`task-assist-staged${selfServeOnly ? ' is-self-serve' : ''}`}
          role="region"
          aria-label={es ? 'Acciones pendientes' : 'Staged actions'}
        >
          <h5>
            {selfServeOnly
              ? (es ? 'Búsqueda automática no terminó — auto-servicio' : 'Automatic lookup did not finish — self-serve')
              : (es ? 'Apruebe para ejecutar en NextCompli' : 'Approve to run inside NextCompli')}
          </h5>
          <p className="task-assist-staged-help">
            {selfServeOnly
              ? (es
                ? 'La búsqueda no devolvió estado. Abra la búsqueda pública (sin inicio de sesión de contribuyente), guarde el PDF e apruebe para guardarlo en la bóveda. No invente un número de certificado.'
                : 'The lookup did not return a status. Open the public search (no taxpayer login), save the PDF, then approve to save it in the vault. Do not invent a certificate number.')
              : (es
                ? 'Solo correo y archivo en portal esperan aquí. Las búsquedas y la bóveda ya corrieron arriba en la historia.'
                : 'Only email and portal filing wait here. Lookups and vault work already ran in the story above.')}
          </p>
          <ul className="task-assist-staged-list">
            {actions.filter((row) => row.status === 'staged').map((action) => {
              const email = action.payload?.email;
              const draftDoc = action.payload?.draft;
              const draftText = String(draftDoc?.text || '');
              // Hide boilerplate ACTION DRAFT dumps — operators only need email / real drafts.
              const showDraft = Boolean(
                draftText
                && !/^NEXTCOMPLI ACTION DRAFT/i.test(draftText.trim())
                && !/^Environment matches —/i.test(String(draftDoc?.title || '')),
              );
              return (
                <li key={action.action_id}>
                  <strong>{action.title}</strong>
                  {action.detail ? <p>{action.detail}</p> : null}
                  {email ? (
                    <pre className="task-assist-email-preview">
                      {`To: ${email.to || '—'}\nSubject: ${email.subject || '—'}\n\n${email.body || ''}`}
                    </pre>
                  ) : null}
                  {showDraft ? (
                    <pre className="task-assist-email-preview">{draftText.slice(0, 1200)}</pre>
                  ) : null}
                  <div className="task-assist-actions">
                    <button
                      type="button"
                      className="dashboard-btn dashboard-btn-primary"
                      disabled={Boolean(actionBusy)}
                      onClick={() => void decideAction(action.action_id, 'approved')}
                    >
                      {actionBusy === action.action_id
                        ? (es ? 'Ejecutando…' : 'Running…')
                        : action.action_type === 'email'
                          ? (es ? 'Aprobar y enviar' : 'Approve & send')
                          : action.action_type === 'upload_evidence'
                            ? (es ? 'Aprobar y guardar en bóveda' : 'Approve & save to vault')
                            : action.action_type === 'portal_file'
                              ? (es ? 'Aprobar y archivar' : 'Approve & file')
                              : (es ? 'Aprobar y ejecutar' : 'Approve & run')}
                    </button>
                    <button
                      type="button"
                      className="dashboard-btn"
                      disabled={Boolean(actionBusy)}
                      onClick={() => void decideAction(action.action_id, 'rejected')}
                    >
                      {es ? 'Rechazar' : 'Reject'}
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      ) : null}

      <details className="task-assist-more">
        <summary>{es ? 'Más opciones (papel, sitio oficial, otra vez)' : 'More options (paper, official site, run again)'}</summary>
        <div className="task-assist-actions">
          {base && accessToken && (kind || agentSkill) ? (
            <button
              type="button"
              className="dashboard-btn"
              disabled={agentBusy}
              onClick={() => void askAgent()}
            >
              {agentBusy
                ? (es ? 'Ejecutando…' : 'Running…')
                : (es ? 'Ejecutar de nuevo' : 'Run again')}
            </button>
          ) : null}
          <button
            type="button"
            className="dashboard-btn"
            onClick={() => document.getElementById(fileId)?.click()}
          >
            {es ? 'Tengo el papel' : 'I have the paper'}
          </button>
          {workUrl ? (
            <button type="button" className="dashboard-btn" onClick={() => openOfficialPage(workUrl)}>
              {item.docsLabel || item.sourceLabel || (es ? 'Abrir página de trabajo' : 'Open work page')}
            </button>
          ) : null}
          {showInstructionsLink ? (
            <a className="dashboard-btn" href={instructionsUrl} target="_blank" rel="noopener noreferrer">
              {es ? 'Instrucciones oficiales' : 'Official instructions'}
            </a>
          ) : null}
        </div>
        {noteOk && part ? (
          <label className="task-assist-note">
            <span>{es ? 'Nota verdadera' : 'True note'}</span>
            <textarea
              value={draft}
              rows={3}
              placeholder={es ? 'Escribe lo que es verdad. No inventes un papel.' : 'Write what is true. Do not make up a paper.'}
              onChange={(event) => setDraft(event.target.value)}
            />
          </label>
        ) : null}
        {noteOk && part ? (
          <div className="task-assist-actions">
            <button
              type="button"
              className="dashboard-btn"
              disabled={!draft.trim() || !onSaveStep}
              onClick={() => {
                const note = draft.trim();
                if (!note || !onSaveStep || !part) return;
                onSaveStep(part.id, {
                  done: true,
                  doneAt: new Date().toISOString(),
                  inferred: false,
                  evidenceNote: note,
                });
                setSaved(note);
              }}
            >
              {saved ? (es ? 'Nota guardada' : 'Note saved') : (es ? 'Guardar esta nota' : 'Save this note')}
            </button>
          </div>
        ) : null}
        {pageSummary ? (
          <p className="task-assist-ask">
            <DodTermText text={pageSummary} language={language} seen={seenTerms} />
          </p>
        ) : null}
      </details>

      <input
        id={fileId}
        type="file"
        hidden
        accept="image/*,.pdf,application/pdf"
        onChange={(event) => {
          const file = event.target.files?.[0];
          event.target.value = '';
          if (!file || !onSaveStep || !part) return;
          const reader = new FileReader();
          reader.onload = () => {
            const dataUrl = typeof reader.result === 'string' ? reader.result : '';
            onSaveStep(part.id, {
              done: true,
              doneAt: new Date().toISOString(),
              inferred: false,
              evidenceName: file.name,
              evidenceMime: file.type || 'application/octet-stream',
              evidenceDataUrl: dataUrl.slice(0, 2_500_000),
              evidenceNote: es ? `Archivo subido: ${file.name}` : `Uploaded file: ${file.name}`,
            });
            setSaved(file.name);
          };
          reader.readAsDataURL(file);
        }}
      />
    </section>
  );
};

export const taskFileInputId = (itemId: string, stepId: string): string => `task-file-${itemId}-${stepId}`;
